import { useEffect, useMemo, useRef, useState, type Ref, type RefObject } from "react";

import { api, ApiError, type ClipView } from "./api";
import { FisheyeOverlay, useFisheyeGeometry } from "./components/FisheyePlayer";
import {
  connectDirect,
  directSupported,
  type DirectConnection,
} from "./direct";
import { friendlyError } from "./errors";
import { useRole } from "./hooks/useRole";

/**
 * Recorder (or simulation file) bitstream on a canvas, same transport as
 * CameraTimeline. The session is opened when `cameraId` is set — not on the
 * first Play — so a click is a seek on a warm socket.
 */
export function useDirectReplay(cameraId: string | undefined) {
  const { role } = useRole();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const directRef = useRef<DirectConnection | null>(null);
  const blockedRef = useRef(false);
  const openingRef = useRef<Promise<DirectConnection | null> | null>(null);
  const cameraRef = useRef(cameraId);
  cameraRef.current = cameraId;
  const [playing, setPlaying] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);

  async function ensure(signal?: AbortSignal): Promise<DirectConnection | null> {
    if (!cameraId || !canvasRef.current) return null;
    if (!directSupported()) {
      setError("This browser cannot decode the recording.");
      return null;
    }
    if (blockedRef.current) return null;
    if (directRef.current) return directRef.current;
    if (openingRef.current) return openingRef.current;
    const opening: Promise<DirectConnection | null> = (async () => {
      try {
        const opened = await api.streams.open(
          { camera_id: cameraId, kind: "replay" },
          signal,
        );
        if (signal?.aborted || cameraRef.current !== cameraId) return null;
        if (!canvasRef.current) return null;
        const conn = connectDirect(opened.session, canvasRef.current, {
          onError: (message) => setError(message),
          onFirstFrame: () => {
            setPlaying(true);
            setPending(false);
          },
          onEnded: () => setPending(false),
        });
        directRef.current = conn;
        return conn;
      } catch (err) {
        if (signal?.aborted) return null;
        blockedRef.current = true;
        setError(
          err instanceof ApiError
            ? friendlyError(err, role, "Could not play that moment.")
            : "Could not play that moment.",
        );
        return null;
      } finally {
        openingRef.current = null;
      }
    })();
    openingRef.current = opening;
    return opening;
  }

  useEffect(() => {
    blockedRef.current = false;
    setError(null);
    setPlaying(false);
    setPending(false);
    setStartedAt(null);
    const ac = new AbortController();
    if (cameraId) void ensure(ac.signal);
    return () => {
      ac.abort();
      directRef.current?.close();
      directRef.current = null;
      setPlaying(false);
    };
    // ensure reads the current cameraId; re-run when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId]);

  return {
    canvasRef: canvasRef as RefObject<HTMLCanvasElement | null>,
    playing,
    pending,
    error,
    startedAt,
    setError,
    seek: async (startMs: number) => {
      blockedRef.current = false;
      setError(null);
      setPending(true);
      setStartedAt(startMs);
      const direct = await ensure();
      if (!direct) {
        setPending(false);
        return;
      }
      direct.seek(startMs);
    },
    stop: () => {
      directRef.current?.stop();
      setPlaying(false);
      setPending(false);
      setStartedAt(null);
    },
    mediaTimeMs: () => directRef.current?.mediaTimeMs() ?? 0,
  };
}

export type DirectReplay = ReturnType<typeof useDirectReplay>;

/** The canvas a clip is decoded onto. For a fisheye camera, or a view cut
 *  from one, the recording is the raw disk; `cameraId` lets it be shown
 *  dewarped (the fisheye with look-around, a view at its pose). */
export function ClipPlayerCanvas({
  canvasRef,
  visible,
  cameraId,
  view,
}: {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  visible: boolean;
  cameraId?: string;
  /** A saved clip's own view: it plays as it was cut, not as a free look. */
  view?: ClipView | null;
}) {
  const geometry = useFisheyeGeometry(cameraId);
  const fisheye = useMemo(
    () => (geometry && view ? { lens: geometry.lens, view } : geometry),
    [geometry, view],
  );
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => setCanvasEl(canvasRef.current), [canvasRef]);
  return (
    <>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <canvas
          ref={canvasRef as Ref<HTMLCanvasElement>}
          className={
            "block min-h-0 min-w-0 max-h-full max-w-full " +
            (visible && !fisheye ? "" : "invisible")
          }
        />
      </div>
      {fisheye && visible && (
        <FisheyeOverlay geometry={fisheye} cameraId={cameraId} canvas={canvasEl} layoutsAt="right-2 top-2" />
      )}
    </>
  );
}
